import { Button } from '@/components/Button';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { useTranslation } from '@/i18n/useTranslation';
import { DOOR, useKioskSession } from '@/lib/kioskSession';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

/**
 * The two front doors [KID-1; Figma · 02 · Have you applied?, `12055:10222` · `10237` · `10252`].
 *
 * **The design split `01 · Welcome` in two on 2026-09-02.** Welcome became a single "Tap to start" door and
 * the new-or-returning question moved here, onto its own card. KID-1 is `approved` and `shipped` and says
 * the kiosk shows *"exactly two doors"*; it still does — one screen later. What the story governs (that the
 * question is asked before check-in, and that the answer only chooses copy) is untouched, so this is the
 * frame deciding layout, which is its half of § 8.
 *
 * **The copy is the Ground Truth's, and the Ground Truth now adopts the frame's strings.** [Decision 0125]
 * (`approved`, 2026-09-01) records the two doors verbatim — *"I've previously applied/worked at Integrity."*
 * and *"I've never applied with Integrity before."* — as validated by Heidi Carrel inside the Figma file,
 * and reverses the order so the returning door comes first. ⚠️ [0070] had rejected the "worked" clause and
 * handed the final string to ISS; ISS supplied one that keeps it. 0125 is the newest non-superseded
 * decision and is what is built here; 0070 is owed an update, which is raised rather than resolved in code.
 *
 * The door only sets copy. `openCheckIn` consumes it and stores a translation key; the door value itself
 * never reaches state, so nothing downstream can diagnose from it [decision 0001].
 */
export function HaveYouAppliedScreen() {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const openCheckIn = useKioskSession((state) => state.openCheckIn);

  return (
    <KioskScreen>
      <KioskCard
        contentWidth={488}
        footer={
          /*
           * A list, though the design draws two loose buttons: it announces "list, 2 items" to a screen
           * reader, which is how someone who cannot see the card learns there are exactly two ways in and no
           * third. Purely additive — it changes nothing visually.
           */
          <ul className="flex w-full list-none flex-col gap-3 p-0">
            <li>
              <Button
                fullWidth
                onClick={() => {
                  openCheckIn(DOOR.returning);
                }}
              >
                {t('welcome.returning')}
              </Button>
            </li>
            <li>
              <Button
                fullWidth
                onClick={() => {
                  openCheckIn(DOOR.new);
                }}
                variant="secondary"
              >
                {t('welcome.newApplicant')}
              </Button>
            </li>
          </ul>
        }
      >
        {/*
          24px above the heading and the type one step up from the form screens: the frame draws the group
          488x92 — a 32-tall title over a 52-tall subtitle with 8 between — opening at y=275 inside a band
          that starts at 251. This card carries no header, so the gap has to come from here.
        */}
        <div className="flex w-full flex-col items-center gap-2 pb-2 pt-6 text-center sm:px-6 min-[1440px]:px-0">
          <h1
            className="kiosk-heading-focus w-full text-2xl font-bold leading-8 tracking-[-0.36px] text-black"
            ref={heading}
            tabIndex={-1}
          >
            {t('applied.heading')}
          </h1>
          <p className="w-full text-lg leading-[26px] text-[var(--muted-foreground)]">
            {t('applied.subtitle')}
          </p>
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
